import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { store } from './store/store.js';
import { startSessionTimeout, resetSessionTimeout, logout } from './store/slices/authSlice';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoginScreen from './components/auth/LoginScreen';
import SubscriptionExpired from './components/subscription/SubscriptionExpired';
import Dashboard from './components/dashboard/Dashboard';
import POS from './components/pos/POS';
import Inventory from './components/inventory/Inventory';
import Reports from './components/reports/Reports';
import Sales from './components/sales/Sales';
import Settings from './components/settings/Settings';
import Sessions from './components/sessions/Sessions';
import Audit from './components/audit/Audit';
import Refunds from './components/refunds/Refunds';
import Expenses from './components/expenses/Expenses';
import Purchases from './components/purchases/Purchases';
import Customers from './components/customers/Customers';
import DeliveryPersons from './components/deliveryPersons/DeliveryPersons';
import MonthlyClosing from './components/monthlyClosing/MonthlyClosing';
import Offers from './components/offers/Offers';
import Layout from './components/layout/Layout';
import apiService from './services/api';
import { isAdminOrOwner, isManagerOrAbove } from './lib/roles';
import './App.css';

function AppRouter() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const [storeBlocked, setStoreBlocked] = useState(null);

  useEffect(() => {
    document.body.className = mode === 'dark' ? 'dark' : mode === 'black' ? 'black' : '';
  }, [mode]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const checkStore = async () => {
      try {
        await apiService.getStoreStatus();
        setStoreBlocked(null);
      } catch (err) {
        if (err.status === 403 && err.body?.code) {
          setStoreBlocked(err.body);
        }
      }
    };

    checkStore();
    const interval = setInterval(checkStore, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(startSessionTimeout());
      const reset = () => dispatch(resetSessionTimeout());
      ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach((ev) => document.addEventListener(ev, reset, true));
      return () => ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach((ev) => document.removeEventListener(ev, reset, true));
    }
    return undefined;
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const handleFocus = (e) => {
      const tag = e.target.tagName;
      const type = e.target.type;
      if (tag === 'INPUT' && (type === 'text' || type === 'number' || type === 'tel' || type === 'url' || type === 'email' || type === 'password')) {
        e.target.select();
      }
    };
    document.addEventListener('focus', handleFocus, true);
    return () => document.removeEventListener('focus', handleFocus, true);
  }, []);

  if (storeBlocked) {
    return <SubscriptionExpired details={storeBlocked} onLogout={() => dispatch(logout())} />;
  }

  if (!isAuthenticated) return <LoginScreen />;

  const isManagerOrAdmin = isManagerOrAbove(user?.role);
  const isAdmin = isAdminOrOwner(user?.role);

  return (
    <Layout>
      <Routes>
        {isManagerOrAdmin ? (
          <>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/sessions" element={<Sessions />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/purchases" element={<Purchases />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/delivery-persons" element={<DeliveryPersons />} />
            <Route path="/monthly-closing" element={<MonthlyClosing />} />
            {isAdmin && <Route path="/audit" element={<Audit />} />}
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Navigate to="/pos" />} />
            <Route path="/pos" element={<POS />} />
            <Route path="*" element={<Navigate to="/pos" />} />
          </>
        )}
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <LanguageProvider>
          <ToastProvider>
            <div className="App">
              <AppRouter />
            </div>
          </ToastProvider>
        </LanguageProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
